import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Button, Modal, OverlayTrigger, Table, Tooltip } from 'react-bootstrap';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import { withTranslation } from 'react-i18next';
import { BsFillSquareFill, BsInfoCircle } from 'react-icons/bs';
import { RiFileExcel2Line } from 'react-icons/ri';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { LoaderContext } from '../../../App';
import { LS_REGULATION_SELECTED_KEY } from '../../../core/constants/common';
import useHasPermission from '../../../core/hooks/useHasPermission';
import HomePageService from '../../../core/service/HomePageService';
import ReportService from '../../../core/service/ReportService';
import Utils from '../../../core/utilities/Utils';
import './DataCollectionProgress.scss';
import './HomePage.scss';

const VIEW_YEARLY = 'yearly';
const VIEW_MONTHLY = 'monthly';
const TAB_UNANSWERED = 'unanswered';
const TAB_BRANCHES = 'branches';

const MONTH_CODES = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

const MONTH_NUM_TO_LABEL_KEY = {
    1: 'January.label',
    2: 'February.label',
    3: 'March.label',
    4: 'April.label',
    5: 'May.label',
    6: 'June.label',
    7: 'July.label',
    8: 'August.label',
    9: 'September.label',
    10: 'October.label',
    11: 'November.label',
    12: 'December.label',
};

const getPeriodLabel = (periodStart, t) => {
    if (!periodStart) return '';
    const [year, monthPart] = periodStart.split('-');
    const monthNum = parseInt(monthPart, 10);
    const monthLabelKey = MONTH_NUM_TO_LABEL_KEY[monthNum];
    if (!year || !monthLabelKey) return '';
    return `${year} ${t(monthLabelKey)}`;
};

const periodStartToApiPeriod = (periodStart) => {
    if (!periodStart) return null;
    const [year, monthPart] = periodStart.split('-');
    const monthIndex = parseInt(monthPart, 10) - 1;
    if (!year || monthIndex < 0 || monthIndex > 11) return null;
    return { [year]: [MONTH_CODES[monthIndex]] };
};

const getMonthPeriodBounds = (periodStart) => {
    if (!periodStart) return { periodStart: '', periodEnd: '' };
    const [yearStr, monthStr] = periodStart.split('-');
    const year = parseInt(yearStr, 10);
    const month = parseInt(monthStr, 10);
    if (!year || !month) return { periodStart: '', periodEnd: '' };
    const lastDay = new Date(year, month, 0).getDate();
    return {
        periodStart,
        periodEnd: `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`,
    };
};

const getProgressPercentage = (entry) => {
    if (!entry) return null;
    if (entry.percentage != null && !Number.isNaN(entry.percentage)) {
        return entry.percentage;
    }
    const total = entry.total ?? 0;
    if (total === 0) return 0;
    return ((entry.completed ?? 0) / total) * 100;
};

const averageProgressPercentages = (entryA, entryB) => {
    const percentages = [entryA, entryB]
        .map(getProgressPercentage)
        .filter((value) => value != null);

    if (percentages.length === 0) return 0;
    return percentages.reduce((sum, value) => sum + value, 0) / percentages.length;
};


const getBranchPercentage = (branch) => {
    const qualEntry = branch?.qualProgress?.[0];
    const quantEntry = branch?.quantProgress?.[0];
    return averageProgressPercentages(qualEntry, quantEntry);
};

const getProgressColor = (percentage) => {
    if (percentage >= 75) return '#5abb6a';
    if (percentage >= 50) return '#FFBF00';
    return '#ef4838';
};

const DataCollectionProgress = ({ t, selectedYear, displayYear, periodParams }) => {
    const yearLabel = displayYear ?? selectedYear;
    const navigate = useNavigate();
    const { setLoaderDisplay } = useContext(LoaderContext);
    const { hasDataCollectionViewPermission } = useHasPermission();
    const [progressData, setProgressData] = useState(null);
    const [viewMode, setViewMode] = useState(VIEW_YEARLY);
    const [showMonthModal, setShowMonthModal] = useState(false);
    const [activeTab, setActiveTab] = useState(TAB_BRANCHES);
    const [selectedMonth, setSelectedMonth] = useState(null);
    const [unansweredDataPoints, setUnansweredDataPoints] = useState([]);
    const [unansweredLoading, setUnansweredLoading] = useState(false);
    const [unansweredError, setUnansweredError] = useState('');
    const [branchRows, setBranchRows] = useState([]);
    const [branchLoading, setBranchLoading] = useState(false);
    const [branchError, setBranchError] = useState('');
    const [parentBranch, setParentBranch] = useState(null);
    const glbRegulationId = localStorage.getItem(LS_REGULATION_SELECTED_KEY);

    useEffect(() => {
        if (!glbRegulationId || !selectedYear) return;

        setLoaderDisplay(true);
        const { periodStart, periodEnd } = periodParams || {};
        const period = `${selectedYear}-01-01`;

        ReportService.getDataCollectionProgress(glbRegulationId, period, periodStart, periodEnd)
            .then((res) => {
                setLoaderDisplay(false);
                if (res.status !== 200) return;
                setProgressData(res.data?.data ?? null);
            })
            .catch(() => {
                setLoaderDisplay(false);
            });
    }, [glbRegulationId, selectedYear, periodParams, setLoaderDisplay]);

    const monthlyPeriodicProgress = useMemo(() => {
        if (!progressData) return [];

        const qualitative = progressData.qualitative ?? [];
        const quantitative = progressData.quantitative ?? [];

        return qualitative.map((qualEntry, index) => {
            const quantEntry = quantitative[index];
            const percentage = averageProgressPercentages(qualEntry, quantEntry);
            return {
                monthIndex: qualEntry?.monthIndex ?? index + 1,
                periodStart: qualEntry?.periodStart ?? '',
                periodLabel: getPeriodLabel(qualEntry?.periodStart, t),
                percentage,
            };
        });
    }, [progressData, t]);

    const yearlyPeriodicPercentage = useMemo(() => {
        if (!monthlyPeriodicProgress.length) return 0;
        const sum = monthlyPeriodicProgress.reduce((acc, item) => acc + item.percentage, 0);
        return sum / monthlyPeriodicProgress.length;
    }, [monthlyPeriodicProgress]);

    const yearlyOneTimePercentage = useMemo(() => {
        if (!progressData) return 0;
        return averageProgressPercentages(progressData.oneTimeQual, progressData.oneTimeGD);
    }, [progressData]);

    const visibleBranchRows = useMemo(
        () => branchRows.filter((branch) => branch?.id != null && (branch?.name || branch?.branchName)),
        [branchRows]
    );

    const gotoDataManagement = (e) => {
        e?.preventDefault();
        navigate('/company_admin/dataview');
    };

    const closeMonthModal = () => {
        setShowMonthModal(false);
        setActiveTab(TAB_BRANCHES);
        setSelectedMonth(null);
        setUnansweredDataPoints([]);
        setUnansweredError('');
        setUnansweredLoading(false);
        setBranchRows([]);
        setBranchError('');
        setBranchLoading(false);
        setParentBranch(null);
    };

    /* Temporarily hidden — unanswered items tab (re-enable later)
    const fetchUnansweredDataPoints = (periodStart) => {
        const standardId = parseInt(glbRegulationId, 10);
        const period = periodStartToApiPeriod(periodStart);

        if (!standardId || !period) {
            setUnansweredError(t('DataCollectionProgress.unansweredModal.error.label'));
            return;
        }

        setUnansweredLoading(true);
        setUnansweredError('');
        setUnansweredDataPoints([]);

        HomePageService.getBranchUnansweredDataPoints({
            standardId,
            periodType: 'MONTHLY',
            period,
            unanswered: true,
            standardWide: true,
        })
            .then((res) => {
                setUnansweredLoading(false);
                const body = res.data;
                const list = Array.isArray(body) ? body : body?.data ?? [];
                setUnansweredDataPoints(Array.isArray(list) ? list : []);
            })
            .catch(() => {
                setUnansweredLoading(false);
                setUnansweredDataPoints([]);
                setUnansweredError(t('DataCollectionProgress.unansweredModal.error.label'));
            });
    };
    */

    const fetchBranchProgress = (periodStart, options = {}) => {
        const { periodStart: monthStart, periodEnd: monthEnd } = getMonthPeriodBounds(periodStart);
        if (!glbRegulationId || !monthStart || !monthEnd) {
            setBranchError(t('DataCollectionProgress.branchModal.error.label'));
            return;
        }

        setBranchLoading(true);
        setBranchError('');
        setBranchRows([]);

        ReportService.getDataCollectionProgress(
            glbRegulationId,
            monthStart,
            monthStart,
            monthEnd,
            options
        )
            .then((res) => {
                setBranchLoading(false);
                if (res.status !== 200) {
                    setBranchError(t('DataCollectionProgress.branchModal.error.label'));
                    return;
                }
                setBranchRows(res.data?.data?.branches ?? []);
            })
            .catch(() => {
                setBranchLoading(false);
                setBranchError(t('DataCollectionProgress.branchModal.error.label'));
            });
    };

    const handleMonthClick = (item) => {
        setSelectedMonth(item);
        setActiveTab(TAB_BRANCHES);
        setParentBranch(null);
        setBranchRows([]);
        setBranchError('');
        setUnansweredDataPoints([]);
        setUnansweredError('');
        setShowMonthModal(true);
        fetchBranchProgress(item.periodStart, { forParentBranches: true });
    };

    const handleTabSelect = (tab) => {
        setActiveTab(tab);
        if (!selectedMonth?.periodStart) return;

        if (tab === TAB_BRANCHES) {
            setParentBranch(null);
            fetchBranchProgress(selectedMonth.periodStart, { forParentBranches: true });
        }
        /* Temporarily hidden — unanswered items tab (re-enable later)
        else if (tab === TAB_UNANSWERED) {
            fetchUnansweredDataPoints(selectedMonth.periodStart);
        }
        */
    };

    const handleBranchClick = (branch) => {
        if (!selectedMonth?.periodStart) return;
        setParentBranch(branch);
        fetchBranchProgress(selectedMonth.periodStart, {
            forParentBranches: false,
            parentBranchId: branch.id,
        });
    };

    const handleBranchBack = () => {
        if (!selectedMonth?.periodStart) return;
        setParentBranch(null);
        fetchBranchProgress(selectedMonth.periodStart, { forParentBranches: true });
    };

    const handleMonthKeyDown = (event, item) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleMonthClick(item);
        }
    };

    const handleBranchKeyDown = (event, branch) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleBranchClick(branch);
        }
    };

    /* Temporarily hidden — unanswered items tab (re-enable later)
    const handleDownloadExcel = () => {
        if (!unansweredDataPoints.length) return;

        const headers = [
            t('UnreportedDataPointsHome.datapointCode.label'),
            t('UnreportedDataPointsHome.datapointName.label'),
        ];
        const excelRows = unansweredDataPoints.map((dp) => [
            dp.dataPointCode || dp.code || '',
            dp.name || '',
        ]);

        const workbook = XLSX.utils.book_new();
        const worksheet = XLSX.utils.aoa_to_sheet([headers, ...excelRows]);
        worksheet['!cols'] = [{ wch: 16 }, { wch: 48 }];
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Data');

        const periodSlug = (selectedMonth?.periodStart || 'period').replace(/-/g, '_');
        XLSX.writeFile(workbook, `Unanswered_data_points_${periodSlug}.xlsx`);
    };
    */

    const renderProgressBar = (label, percentage, key, onClick, onKeyDown) => {
        const roundedPercentage = Math.round(percentage);
        const fillColor = getProgressColor(percentage);
        const isClickable = Boolean(onClick);
        const barTrack = (
            <div className="data-collection-progress-monthly__bar-track">
                <div
                    className="data-collection-progress-monthly__bar-fill"
                    style={{
                        width: `${Math.max(roundedPercentage, roundedPercentage > 0 ? 8 : 0)}%`,
                        backgroundColor: fillColor,
                    }}
                >
                    <span className="data-collection-progress-monthly__bar-text">{roundedPercentage}%</span>
                </div>
                {roundedPercentage === 0 && (
                    <span className="data-collection-progress-monthly__bar-text data-collection-progress-monthly__bar-text--zero">
                        0%
                    </span>
                )}
            </div>
        );

        return (
            <div
                className={`data-collection-progress-monthly__row${isClickable ? ' data-collection-progress-monthly__row--clickable' : ''}`}
                key={key}
                role={isClickable ? 'button' : undefined}
                tabIndex={isClickable ? 0 : undefined}
                onClick={onClick}
                onKeyDown={onKeyDown}
            >
                <span className="data-collection-progress-monthly__label">{label}</span>
                {barTrack}
            </div>
        );
    };

    const renderBranchProgressBar = (branch, onClick, onKeyDown) => {
        const label = branch.name || branch.branchName || '';
        const percentage = getBranchPercentage(branch);
        const roundedPercentage = Math.round(percentage);
        const fillColor = getProgressColor(percentage);
        const isClickable = Boolean(onClick);

        return (
            <div
                className={`data-collection-progress-branch-list__row${isClickable ? ' data-collection-progress-branch-list__row--clickable' : ''}`}
                key={`branch-${branch.id}`}
                role={isClickable ? 'button' : undefined}
                tabIndex={isClickable ? 0 : undefined}
                onClick={onClick}
                onKeyDown={onKeyDown}
            >
                <span className="data-collection-progress-branch-list__name" title={label}>
                    {label}
                </span>
                <div className="data-collection-progress-monthly__bar-track">
                    <div
                        className="data-collection-progress-monthly__bar-fill"
                        style={{
                            width: `${Math.max(roundedPercentage, roundedPercentage > 0 ? 8 : 0)}%`,
                            backgroundColor: fillColor,
                        }}
                    >
                        <span className="data-collection-progress-monthly__bar-text">{roundedPercentage}%</span>
                    </div>
                    {roundedPercentage === 0 && (
                        <span className="data-collection-progress-monthly__bar-text data-collection-progress-monthly__bar-text--zero">
                            0%
                        </span>
                    )}
                </div>
            </div>
        );
    };

    const renderViewDescription = () => (
        <p
            className="data-collection-progress-description"
            data-testid="data-collection-progress-view-description"
        >
            {viewMode === VIEW_YEARLY
                ? t('DataCollectionProgress.yearlyDescription.label', { year: yearLabel })
                : t('DataCollectionProgress.monthlyDescription.label', { year: yearLabel })}
        </p>
    );

    const renderViewToggle = () => (
        <div className="data-collection-progress-toggle" data-testid="data-collection-progress-toggle">
            <button
                type="button"
                className={`data-collection-progress-toggle__btn ${viewMode === VIEW_YEARLY ? 'active' : ''}`}
                onClick={() => setViewMode(VIEW_YEARLY)}
                aria-pressed={viewMode === VIEW_YEARLY}
            >
                {t('Yearly.label')}
            </button>
            <button
                type="button"
                className={`data-collection-progress-toggle__btn ${viewMode === VIEW_MONTHLY ? 'active' : ''}`}
                onClick={() => setViewMode(VIEW_MONTHLY)}
                aria-pressed={viewMode === VIEW_MONTHLY}
            >
                {t('Monthly.label')}
            </button>
        </div>
    );

    const renderLegend = () => (
        <OverlayTrigger
            placement="top"
            overlay={
                <Tooltip>
                    <div dangerouslySetInnerHTML={{ __html: t('ProgressStatusBox.tooltip.label') }} />
                </Tooltip>
            }
        >
            <div className="progress_status_box">
                <ul>
                    <li><BsFillSquareFill className="text-Medium font-14" /> 75 - 100%</li>
                    <li><BsFillSquareFill className="text-High font-14" /> 50 - 75%</li>
                    <li><BsFillSquareFill className="text-danger font-14" /> 0 - 50%</li>
                </ul>
            </div>
        </OverlayTrigger>
    );

    const renderYearlyView = () => (
        <div className="HomeCardBodyIn">
            <OverlayTrigger placement="top" overlay={Utils.renderTooltip(t('Periodic.tooltip.label'))}>
                <div className="Home_DatablockCard_progress">
                    <CircularProgressbar
                        value={yearlyPeriodicPercentage}
                        text={`${Math.round(yearlyPeriodicPercentage)}%`}
                        styles={buildStyles({
                            pathColor: getProgressColor(yearlyPeriodicPercentage),
                            textColor: getProgressColor(yearlyPeriodicPercentage),
                            trailColor: '#dee8ed',
                        })}
                    />
                    <h6 className="text-center p-2 mb-0">{t('Periodic.label')}</h6>
                </div>
            </OverlayTrigger>
            <OverlayTrigger
                placement="top"
                overlay={
                    <Tooltip>
                        <div dangerouslySetInnerHTML={{ __html: t('OneTime.tooltip.label').replace(/\n/g, '<br />') }} />
                    </Tooltip>
                }
            >
                <div className="Home_DatablockCard_progress">
                    <CircularProgressbar
                        value={yearlyOneTimePercentage}
                        text={`${Math.round(yearlyOneTimePercentage)}%`}
                        styles={buildStyles({
                            pathColor: getProgressColor(yearlyOneTimePercentage),
                            textColor: getProgressColor(yearlyOneTimePercentage),
                            trailColor: '#dee8ed',
                        })}
                    />
                    <h6 className="text-center p-2 mb-0">{t('OneTime.label')}</h6>
                </div>
            </OverlayTrigger>
            {renderLegend()}
        </div>
    );

    const renderMonthlyView = () => (
        <div className="data-collection-progress-monthly" data-testid="data-collection-progress-monthly">
            {monthlyPeriodicProgress.map((item) => {
                const roundedPercentage = Math.round(item.percentage);
                const fillColor = getProgressColor(item.percentage);
                return (
                    <div
                        className="data-collection-progress-monthly__row data-collection-progress-monthly__row--clickable"
                        key={`month-${item.monthIndex}`}
                        role="button"
                        tabIndex={0}
                        onClick={() => handleMonthClick(item)}
                        onKeyDown={(event) => handleMonthKeyDown(event, item)}
                        data-testid={`data-collection-progress-month-${item.monthIndex}`}
                    >
                        <span className="data-collection-progress-monthly__label">{item.periodLabel}</span>
                        <div className="data-collection-progress-monthly__bar-track">
                            <div
                                className="data-collection-progress-monthly__bar-fill"
                                style={{
                                    width: `${Math.max(roundedPercentage, roundedPercentage > 0 ? 8 : 0)}%`,
                                    backgroundColor: fillColor,
                                }}
                            >
                                <span className="data-collection-progress-monthly__bar-text">{roundedPercentage}%</span>
                            </div>
                            {roundedPercentage === 0 && (
                                <span className="data-collection-progress-monthly__bar-text data-collection-progress-monthly__bar-text--zero">
                                    0%
                                </span>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );

    const branchTabTitle = parentBranch
        ? t('DataCollectionProgress.branchModal.childTitle.label', {
            period: selectedMonth?.periodLabel ?? '',
            branch: parentBranch.name ?? '',
        })
        : t('DataCollectionProgress.branchModal.title.label', {
            period: selectedMonth?.periodLabel ?? '',
        });

    /* Temporarily hidden — tab bar hidden while unanswered tab is disabled (re-enable later)
    const renderModalTabs = () => (
        <div
            className="data-collection-progress-modal-tabs"
            role="tablist"
            aria-label={t('DataCollectionProgress.monthModal.title.label', {
                period: selectedMonth?.periodLabel ?? '',
            })}
            data-testid="data-collection-progress-modal-tabs"
        >
            <button
                type="button"
                role="tab"
                id="data-collection-progress-tab-branches"
                aria-selected={activeTab === TAB_BRANCHES}
                aria-controls="data-collection-progress-panel-branches"
                className={`data-collection-progress-modal-tabs__btn${activeTab === TAB_BRANCHES ? ' active' : ''}`}
                onClick={() => handleTabSelect(TAB_BRANCHES)}
            >
                {t('DataCollectionProgress.monthModal.branchesTab.label')}
            </button>
            <button
                type="button"
                role="tab"
                id="data-collection-progress-tab-unanswered"
                aria-selected={activeTab === TAB_UNANSWERED}
                aria-controls="data-collection-progress-panel-unanswered"
                className={`data-collection-progress-modal-tabs__btn${activeTab === TAB_UNANSWERED ? ' active' : ''}`}
                onClick={() => handleTabSelect(TAB_UNANSWERED)}
            >
                {t('DataCollectionProgress.monthModal.unansweredTab.label')}
            </button>
        </div>
    );
    */

    const renderBranchesTabPanel = () => (
        <div
            id="data-collection-progress-panel-branches"
            role="tabpanel"
            aria-labelledby="data-collection-progress-tab-branches"
            className="data-collection-progress-modal-tabs__panel"
        >
            <div className="data-collection-progress-branch-panel__header mb-2">
                <p className="text-muted small mb-0">{branchTabTitle}</p>
                {parentBranch && (
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={handleBranchBack}
                        data-testid="data-collection-progress-branch-back"
                        className="data-collection-progress-branch-panel__back-btn"
                    >
                        {t('DataCollectionProgress.branchModal.back.label')}
                    </Button>
                )}
            </div>
            <p
                className="data-collection-progress-description data-collection-progress-description--modal mb-2"
                data-testid="data-collection-progress-branch-description"
            >
                {parentBranch
                    ? t('DataCollectionProgress.branchModal.childDescription.label', {
                        branch: parentBranch.name ?? '',
                        period: selectedMonth?.periodLabel ?? '',
                    })
                    : t('DataCollectionProgress.branchModal.parentDescription.label', {
                        period: selectedMonth?.periodLabel ?? '',
                    })}
            </p>
            {branchLoading && (
                <p className="text-muted mb-0">{t('Loading.label')}</p>
            )}
            {!branchLoading && branchError && (
                <p className="text-danger mb-0" role="alert">{branchError}</p>
            )}
            {!branchLoading && !branchError && visibleBranchRows.length === 0 && (
                <p className="text-muted mb-0">
                    {t('DataCollectionProgress.branchModal.noData.label')}
                </p>
            )}
            {!branchLoading && !branchError && visibleBranchRows.length > 0 && (
                <div
                    className="data-collection-progress-unanswered-modal__scroll data-collection-progress-unanswered-modal__scroll--branches"
                    data-testid="data-collection-progress-branch-list"
                >
                    <div className="data-collection-progress-branch-list">
                        {visibleBranchRows.map((branch) =>
                            renderBranchProgressBar(
                                branch,
                                parentBranch ? undefined : () => handleBranchClick(branch),
                                parentBranch ? undefined : (event) => handleBranchKeyDown(event, branch)
                            )
                        )}
                    </div>
                </div>
            )}
        </div>
    );

    /* Temporarily hidden — unanswered items tab (re-enable later)
    const renderUnansweredTabPanel = () => (
        <div
            id="data-collection-progress-panel-unanswered"
            role="tabpanel"
            aria-labelledby="data-collection-progress-tab-unanswered"
            className="data-collection-progress-modal-tabs__panel"
            hidden={activeTab !== TAB_UNANSWERED}
        >
            {unansweredLoading && (
                <p className="text-muted mb-0">{t('Loading.label')}</p>
            )}
            {!unansweredLoading && unansweredError && (
                <p className="text-danger mb-0" role="alert">{unansweredError}</p>
            )}
            {!unansweredLoading && !unansweredError && unansweredDataPoints.length === 0 && (
                <p className="text-muted mb-0">
                    {t('DataCollectionProgress.unansweredModal.noData.label')}
                </p>
            )}
            {!unansweredLoading && !unansweredError && unansweredDataPoints.length > 0 && (
                <>
                    <p className="text-muted small mb-2" data-testid="data-collection-progress-unanswered-count">
                        {t('DataCollectionProgress.unansweredModal.count.label', {
                            count: unansweredDataPoints.length,
                        })}
                    </p>
                    <div className="data-collection-progress-unanswered-modal__scroll">
                        <Table bordered hover size="sm" className="mb-0">
                            <thead>
                                <tr>
                                    <th>{t('UnreportedDataPointsHome.datapointCode.label')}</th>
                                    <th>{t('UnreportedDataPointsHome.datapointName.label')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {unansweredDataPoints.map((dp) => (
                                    <tr key={dp.id ?? dp.dataPointCode}>
                                        <td>{dp.dataPointCode || dp.code || '-'}</td>
                                        <td>{dp.name || '-'}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </>
            )}
        </div>
    );
    */

    return (
        <div className="HomeCard mb-3 data-collection-progress-card" data-testid="data-collection-progress">
            <div className="HomeCardhead">
                <div className="HomeCardhead_in">
                    <h5>
                        {t('DataCollectionProgress.title.label', { year: yearLabel })}
                        <OverlayTrigger
                            placement="top"
                            overlay={Utils.renderTooltip(t('DataCollectionProgress.tooltip.label', { year: yearLabel }))}
                        >
                            <span className="ms-1"><BsInfoCircle /></span>
                        </OverlayTrigger>
                    </h5>
                    {renderViewToggle()}
                </div>
            </div>
            <div className="HomeCardBody">
                {renderViewDescription()}
                {viewMode === VIEW_YEARLY ? renderYearlyView() : renderMonthlyView()}
            </div>
            <div className="HomeCardFooter" hidden={!hasDataCollectionViewPermission()}>
                {t('ForMoreDetails.label')}{' '}
                <a href="#" onClick={gotoDataManagement}>{t('GoToDataManagement.label')}</a>
            </div>

            <Modal
                show={showMonthModal}
                onHide={closeMonthModal}
                size="lg"
                centered
                className="data-collection-progress-unanswered-modal"
                data-testid="data-collection-progress-unanswered-modal"
            >
                <Modal.Header className="d-flex align-items-center justify-content-between">
                    <Modal.Title>
                        {t('DataCollectionProgress.monthModal.title.label', {
                            period: selectedMonth?.periodLabel ?? '',
                        })}
                    </Modal.Title>
                    <div className="d-flex align-items-center gap-2 flex-shrink-0">
                        {/* Temporarily hidden — unanswered items excel download (re-enable later)
                        {activeTab === TAB_UNANSWERED && (
                            <OverlayTrigger
                                placement="top"
                                overlay={
                                    <Tooltip id="data-collection-progress-excel-tooltip">
                                        {t('DownloadAsExcel.label')}
                                    </Tooltip>
                                }
                            >
                                <span className="d-inline-block">
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={handleDownloadExcel}
                                        disabled={unansweredLoading || unansweredDataPoints.length === 0}
                                        aria-label={t('DownloadAsExcel.label')}
                                        className="d-inline-flex align-items-center justify-content-center px-2"
                                        data-testid="data-collection-progress-excel-download"
                                    >
                                        <RiFileExcel2Line className="text-success" size={20} aria-hidden />
                                    </Button>
                                </span>
                            </OverlayTrigger>
                        )}
                        */}
                        <button
                            type="button"
                            className="btn-close"
                            aria-label={t('UnreportedDataPointsHome.close.label')}
                            onClick={closeMonthModal}
                        />
                    </div>
                </Modal.Header>
                <Modal.Body className="data-collection-progress-unanswered-modal__body">
                    <div className="data-collection-progress-unanswered-modal__tabs">
                        {/* {renderModalTabs()} */}
                        {renderBranchesTabPanel()}
                        {/* {renderUnansweredTabPanel()} */}
                    </div>
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default withTranslation()(DataCollectionProgress);
